import type { Theme } from '@goodtime/engine';
import { buildSlotAssets } from './slotAssets';

const ASSETS = buildSlotAssets('knights', {
  cherry:  'quest_banner',
  lemon:   'lance',
  orange:  'shield_crest',
  bell:    'longsword',
  star:    'knight_wild',
  seven:   'plumed_helm',
  gem:     'war_horse',
  scatter: 'round_table_scatter',
  money:   'holy_grail',
});

/** Knights' Quest — Kingdom alt slot. Blue/silver heraldic palette,
 *  combat-and-quest iconography. Symbol-id mapping mirrors
 *  {@link FOREST_THEME}.
 *
 *  <p><b>Mechanical personality — "Forward Momentum":</b>
 *  <ul>
 *    <li>Every wheel spin pays out — zero segments removed entirely.
 *        The quest never breaks momentum.
 *    <li>The wheel skews moderate (x2 / x3 / x5 dominant) so each
 *        win is meaningfully larger than Royal Court's steady drip
 *        but more reliable than Dragon's Hoard's volatility.
 *    <li>Symbol weights stay close to the Forest baseline so the
 *        reels themselves feel familiar — the differentiator is
 *        the no-zero wheel.
 *  </ul>
 *  Intended feel: heroic ramp. Every spin advances the quest;
 *  nothing's wasted. */
export const KNIGHTS_THEME: Theme = {
  id: 'knights',
  name: "Knights' Quest",
  palette: {
    accent: 0x3e5baf,         // royal blue
    secondary: 0xc8ccd4,      // polished silver
    warm: 0xe8b83a,           // gold trim (win highlight)
    frameFill: 0x1a2040,
    bgGradientTop: 0x0e1428,
    bgGradientBottom: 0x2a3a5a,
    dimOverlay: 0x05080d,
  },
  slot: {
    symbols: [
      { id: 'cherry',  label: 'Banner',     glyph: '🚩', tint: 0x3e5baf, weight: 5,   textureAlias: 'knights:cherry',
        winVideoAlias: 'knights:cherry:win:video', winVideoSpeed: 2 },
      { id: 'lemon',   label: 'Lance',      glyph: '🥖', tint: 0xc73e3e, weight: 5,   textureAlias: 'knights:lemon',
        winVideoAlias: 'knights:lemon:win:video', winVideoSpeed: 2 },
      { id: 'orange',  label: 'Shield',     glyph: '🛡', tint: 0x3e5baf, weight: 4,   textureAlias: 'knights:orange',
        winVideoAlias: 'knights:orange:win:video', winVideoSpeed: 2 },
      { id: 'bell',    label: 'Longsword',  glyph: '⚔️', tint: 0xc8ccd4, weight: 3,   textureAlias: 'knights:bell',
        winVideoAlias: 'knights:bell:win:video', winVideoSpeed: 2 },
      { id: 'star',    label: 'Knight',     glyph: '🤺', tint: 0x3e5baf, weight: 2,   textureAlias: 'knights:star', // wild
        winVideoAlias: 'knights:star:win:video', winVideoSpeed: 2 },
      { id: 'seven',   label: 'Helm',       glyph: '🪖', tint: 0xc8ccd4, weight: 1,   textureAlias: 'knights:seven',
        winVideoAlias: 'knights:seven:win:video', winVideoSpeed: 2 },
      { id: 'gem',     label: 'War Horse',  glyph: '🐎', tint: 0x6b4a1a, weight: 1,   textureAlias: 'knights:gem',
        winVideoAlias: 'knights:gem:win:video', winVideoSpeed: 2 },
      { id: 'scatter', label: 'Round Table',glyph: '⭕', tint: 0x6b4a1a, weight: 1,   textureAlias: 'knights:scatter',
        winVideoAlias: 'knights:scatter:win:video', winVideoSpeed: 2 },
      { id: 'money',   label: 'Grail',      glyph: '🏆', tint: 0xe8b83a, weight: 1.5,
        textureAlias: 'knights:money',
        winVideoAlias: 'knights:money:win:video', winVideoSpeed: 2,
        isMoney: true },
      // Gem-on-reel — producer brief 2026-06-04. Scatter-style bonus
      // shared across every biome (registered via SHARED_REEL_SYMBOLS
      // in themes/index.ts). Weights deliberately rare so a gem
      // landing feels like a treat rather than background noise.
      { id: 'gem_t5',  label: 'Gems',     glyph: '💎', tint: 0x3ea7e8, weight: 0.10, gemValue: 5,  textureAlias: 'shared:reel:gem_5' },
      { id: 'gem_t10', label: 'Gem Pile', glyph: '💎', tint: 0x3ea7e8, weight: 0.05, gemValue: 10, textureAlias: 'shared:reel:gem_10' },
    ],
    wildSymbolId: 'star',
    scatterSymbolId: 'scatter',
  },
  wheel: {
    // No zero segments at all — every spin advances the quest. The
    // x2/x3/x5 cluster dominates so wins feel meaningful without the
    // bust-cliff that Forest and Royal still carry. x10 / x25 stay
    // rare to keep the top end aspirational.
    segments: [
      { id: 'x1-a',   kind: 'multiplier', label: '×1',  tint: 0x3e5baf, multiplier: 1,  weight: 3 },
      { id: 'x2-a',   kind: 'multiplier', label: '×2',  tint: 0xc8ccd4, multiplier: 2,  weight: 4 },
      { id: 'x3-a',   kind: 'multiplier', label: '×3',  tint: 0xe8b83a, multiplier: 3,  weight: 4 },
      { id: 'x2-b',   kind: 'multiplier', label: '×2',  tint: 0xc8ccd4, multiplier: 2,  weight: 4 },
      { id: 'x5-a',   kind: 'multiplier', label: '×5',  tint: 0xe8b83a, multiplier: 5,  weight: 3 },
      { id: 'x1-b',   kind: 'multiplier', label: '×1',  tint: 0x3e5baf, multiplier: 1,  weight: 3 },
      { id: 'x3-b',   kind: 'multiplier', label: '×3',  tint: 0xe8b83a, multiplier: 3,  weight: 3 },
      { id: 'x2-c',   kind: 'multiplier', label: '×2',  tint: 0xc8ccd4, multiplier: 2,  weight: 4 },
      { id: 'x5-b',   kind: 'multiplier', label: '×5',  tint: 0xe8b83a, multiplier: 5,  weight: 2 },
      { id: 'x10-a',  kind: 'multiplier', label: '×10', tint: 0xc8ccd4, multiplier: 10, weight: 1 },
      { id: 'x3-c',   kind: 'multiplier', label: '×3',  tint: 0xe8b83a, multiplier: 3,  weight: 3 },
      { id: 'jackpot',kind: 'multiplier', label: '×25', tint: 0xe8b83a, multiplier: 25, weight: 1 },
    ],
  },
  assets: {
    slot: ASSETS.slot,
    slotWin: ASSETS.slotWin,
    scene: {
      background:       { alias: 'knights:bg',         path: 'environment/knights.png' },
      columnBackground: { alias: 'knights:column_bg',  path: 'reel/knights_column_bg.png' },
      border:           { alias: 'knights:border',     path: 'brand/border_forest.png' },
    },
  },
};
